import { secondAxios } from "../../../../../../../api/axios";
import abbreviationList from "./abbreviation.json";

export const documentAbbreviations = Array.isArray(abbreviationList)
  ? abbreviationList
  : [];

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

const abbreviationByDescription = new Map(
  documentAbbreviations.map((item) => [
    String(item.description ?? "")
      .trim()
      .toUpperCase(),
    String(item.abbreviation ?? "").trim(),
  ]),
);

/** Parse stored values like COR-001 → { abbr: "COR", seq: "001" }. */
export const parseNumberedDocumentUid = (raw) => {
  const value = String(raw ?? "").trim();
  const match = value.match(/^([A-Za-z]+)-(\d+)$/);
  if (!match) return null;
  return { abbr: match[1].toUpperCase(), seq: match[2] };
};

/** Map stored documentName to abbreviation code when possible. */
export const resolveDocumentNameAbbreviation = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const numbered = parseNumberedDocumentUid(value);
  if (numbered) return numbered.abbr;
  if (abbreviationByCode.has(value)) return value;
  const fromDescription = abbreviationByDescription.get(value.toUpperCase());
  return fromDescription ?? value;
};

/** Resolve stored documentName / abbr to the full identifier description. */
export const resolveDocumentNameDescription = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const abbr = resolveDocumentNameAbbreviation(value);
  const known = abbreviationByCode.get(abbr);
  if (known?.description) return String(known.description).trim();
  if (abbreviationByDescription.has(value.toUpperCase())) return value;
  return value;
};

/** Prefer abbreviation codes (no spaces); map descriptions to codes. */
const toUidAbbreviation = (documentNameOrAbbr) => {
  const raw = String(documentNameOrAbbr ?? "").trim();
  if (!raw) return "";
  const resolved = resolveDocumentNameAbbreviation(raw);
  if (!resolved || /\s/.test(resolved)) return "";
  return resolved;
};

/**
 * UID from full document abbreviation + sequence (e.g. PRC + 101 → PRC-101).
 */
export const buildDocumentUid = (documentNameOrAbbr, sequence) => {
  const abbr = toUidAbbreviation(documentNameOrAbbr);
  const seq =
    sequence != null && String(sequence).trim() !== ""
      ? String(sequence).trim()
      : "";
  if (!abbr || !seq) return "";
  return `${abbr}-${seq}`;
};

const readSequenceValue = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const numbered = parseNumberedDocumentUid(
    doc.documentName ?? doc.uid ?? doc.department,
  );
  if (numbered) return numbered.seq;
  const v = doc.sequence ?? doc.sequenceNumber ?? doc.seq ?? doc.number;
  if (v == null || String(v).trim() === "") return "";
  const s = String(v).trim();
  if (/[A-Za-z]/.test(s) || s.includes("-")) return "";
  return s;
};

/** Display/save UID: prefer stored COR-001 style documentName/uid. */
export const resolveDocumentUid = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const fromName = String(doc.documentName ?? "").trim();
  if (parseNumberedDocumentUid(fromName)) return fromName;
  const fromUid = String(doc.uid ?? "").trim();
  if (parseNumberedDocumentUid(fromUid)) return fromUid;

  const abbr = toUidAbbreviation(doc.documentName ?? doc.department ?? doc.uid);
  const seq = readSequenceValue(doc);
  return buildDocumentUid(abbr, seq) || fromName || fromUid;
};

/** Abbreviation used by an existing register row (COR from COR-001 / CLOSE OUT REPORT). */
export const getDocumentRowAbbreviation = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  return toUidAbbreviation(doc.documentName ?? doc.uid ?? doc.department ?? "");
};

/**
 * Next UID for an abbreviation: COR-001, COR-002, …
 * Scans existing register rows for the same abbreviation.
 */
export const allocateNextDocumentUid = (
  abbreviationOrDescription,
  existingDocs = [],
  excludeId,
) => {
  const abbr = toUidAbbreviation(abbreviationOrDescription);
  if (!abbr) return "";

  let max = 0;
  for (const doc of existingDocs || []) {
    if (
      excludeId != null &&
      String(doc?.id ?? "") !== "" &&
      String(doc.id) === String(excludeId)
    ) {
      continue;
    }
    if (getDocumentRowAbbreviation(doc) !== abbr) continue;
    const seq = readSequenceValue(doc);
    const n = seq ? parseInt(seq, 10) : NaN;
    if (Number.isFinite(n) && n > max) max = n;
  }

  return `${abbr}-${String(max + 1).padStart(3, "0")}`;
};

/**
 * Ensure payload has a numbered UID (GAD-001) in documentName + uid.
 * Keeps existing numbered value when abbreviation is unchanged on edit.
 */
export const ensureNumberedDocumentUid = (
  raw,
  existingDocs = [],
  { excludeId } = {},
) => {
  const incoming = String(raw?.documentName ?? raw?.uid ?? raw?.department ?? "").trim();
  const abbr = toUidAbbreviation(incoming);
  if (!abbr) return { ...raw };

  const incomingNumbered = parseNumberedDocumentUid(incoming);
  const existingRow =
    excludeId != null
      ? (existingDocs || []).find((d) => String(d?.id) === String(excludeId))
      : null;
  const existingUid = resolveDocumentUid(existingRow || {});
  const existingAbbr = getDocumentRowAbbreviation(existingRow || {});

  let uid = "";
  if (incomingNumbered && incomingNumbered.abbr === abbr) {
    uid = `${incomingNumbered.abbr}-${incomingNumbered.seq}`;
  } else if (existingAbbr === abbr && parseNumberedDocumentUid(existingUid)) {
    uid = existingUid;
  } else {
    uid = allocateNextDocumentUid(abbr, existingDocs, excludeId);
  }

  return {
    ...raw,
    documentName: uid,
    uid,
    sequence: parseInt(parseNumberedDocumentUid(uid)?.seq ?? "0", 10) || undefined,
  };
};

/**
 * projectNumber + abbreviation + uid
 * e.g. 1001-PDS-V-101
 */
/** UID for schedule/task rows: direct field or match document register by task code / title. */
export function resolveTaskUidForTask(task, documents = []) {
  if (!task) return "";
  const direct = String(task.uid ?? "").trim();
  if (direct) return direct;
  const key = String(task.taskCode ?? task.task ?? "").trim();
  if (!key) return "";
  const doc = (documents || []).find((d) => {
    const title = String(d?.title ?? d?.document ?? "").trim();
    const docNum = String(d?.docNumber ?? d?.documentNumber ?? "").trim();
    return title === key || docNum === key;
  });
  return doc?.uid != null ? String(doc.uid).trim() : "";
}

export const buildRegisterDocNumber = (doc, { wbsLabel } = {}) => {
  if (!doc || typeof doc !== "object") return "";
  const uid = resolveDocumentUid(doc);
  const parts = [doc.projectNumber, wbsLabel, uid]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  return parts.join("-");
};

/** Merge persisted row with in-progress edit values for live Document # preview. */
export const getRegisterDocSource = (row) => {
  const original = row?.original ?? row ?? {};
  const cache = row?._valuesCache ?? {};
  const documentName =
    cache.documentName ?? original.documentName ?? original.department;
  const uid = resolveDocumentUid({
    ...original,
    ...cache,
    documentName,
    uid: cache.uid ?? original.uid,
  });
  return {
    projectNumber: cache.projectNumber ?? original.projectNumber,
    documentName: uid || documentName,
    uid,
  };
};

/** `@IsInt` on CreateDocumentDto — send a JSON number or omit. */
export const normalizeNumberOfSheets = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n =
    typeof value === "number" ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
};

/**
 * Builds body for POST/PATCH per CreateDocumentDto / UpdateDocumentDto (whitelist-safe).
 * Omits blank optional fields; sends integer `numberOfSheets` when set.
 */
export const buildDocumentMutationBody = (
  raw,
  { isCreate, wbsLabel } = { isCreate: false },
) => {
  const trim = (s) => (s == null ? "" : String(s).trim());

  const companyName = trim(raw.companyName);
  const projectNumber = trim(raw.projectNumber);

  // Prefer numbered UID already allocated (COR-001); fall back to legacy fields
  const numbered =
    parseNumberedDocumentUid(raw.documentName) ||
    parseNumberedDocumentUid(raw.uid);
  const uid = numbered
    ? `${numbered.abbr}-${numbered.seq}`
    : resolveDocumentUid(raw);
  const documentName = uid || trim(raw.documentName ?? raw.department);

  const body = {};

  if (raw.type != null && String(raw.type).trim() !== "") {
    body.type = String(raw.type).trim();
  }

  const documentNumber = buildRegisterDocNumber(
    {
      projectNumber,
      documentName,
      uid,
    },
    { wbsLabel },
  );
  if (documentNumber) body.documentNumber = documentNumber;
  if (companyName) body.companyName = companyName;
  if (projectNumber) body.projectNumber = projectNumber;
  // Persist numbered UID in document identifier field
  if (documentName) body.documentName = documentName;
  if (uid !== "") body.uid = uid;

  const seqFromUid = numbered?.seq ?? readSequenceValue(raw);
  if (seqFromUid !== "") {
    const seqNum = parseInt(String(seqFromUid), 10);
    if (Number.isFinite(seqNum)) {
      body.sequence = seqNum;
      body.number = seqNum;
    } else {
      body.sequence = String(seqFromUid);
    }
  }

  const rev = raw.revision;
  if (rev !== undefined && rev !== null && String(rev).trim() !== "") {
    body.revision = String(rev).trim();
  }

  const description = raw.description ?? raw.title;
  if (
    description !== undefined &&
    description !== null &&
    String(description).trim() !== ""
  ) {
    body.description = String(description).trim();
  }

  const numberOfSheets = normalizeNumberOfSheets(raw.numberOfSheets);
  if (numberOfSheets !== undefined) body.numberOfSheets = numberOfSheets;

  const responsible = raw.responsible;
  if (responsible !== undefined && responsible !== null && trim(responsible)) {
    body.responsible = trim(responsible);
  }

  return body;
};

const fetchDocumentsByProject = async (projectId, type) => {
  if (!projectId) return [];
  const documentType = type || "all";
  const response = (
    await secondAxios.get(`/documents/${projectId}/type/${documentType}`)
  ).data;
  return response;
};

/** Dedupe concurrent plant/project document list fetches (plant mode is N+1 HTTP). */
const documentListInflight = new Map();

const runDeduped = (key, loader) => {
  const existing = documentListInflight.get(key);
  if (existing) return existing;

  const promise = loader().finally(() => {
    if (documentListInflight.get(key) === promise) {
      documentListInflight.delete(key);
    }
  });

  documentListInflight.set(key, promise);
  return promise;
};

export const invalidateDocumentListCache = () => {
  documentListInflight.clear();
};

const loadPlantDocumentList = async (type, plantId) => {
  if (!plantId) return [];
  try {
    const res = await secondAxios.get(`/plant/${plantId}`);
    const projectsRaw =
      res.data?.projects ||
      (Array.isArray(res.data?.project)
        ? res.data.project
        : res.data?.project
        ? [res.data.project]
        : Array.isArray(res.data)
        ? res.data
        : []);

    if (!Array.isArray(projectsRaw) || projectsRaw.length === 0) {
      return [];
    }

    const filteredProjects = projectsRaw.filter(
      (p) => p.clientName === process.env.REACT_APP_CLIENT_NAME
    );

    if (filteredProjects.length === 0) {
      return [];
    }

    const docsByProject = await Promise.all(
      filteredProjects.map(async (project) => {
        const docs = await fetchDocumentsByProject(project?.id, type);
        return docs.map((doc) => ({
          ...doc,
          projectId: project?.id,
          projectName: project?.name,
        }));
      })
    );

    const allDocuments = docsByProject.flat();

    const seen = new Set();
    const uniqueDocuments = allDocuments.filter((doc) => {
      const label =
        doc?.documentNumber ??
        doc?.docNumber ??
        doc?.description ??
        doc?.title ??
        "";
      const key = String(label).toLowerCase().trim();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return uniqueDocuments;
  } catch (error) {
    console.error("Failed to load plant documents", error);
    return [];
  }
};

export const getProjectDocumentList = async (type, projectId) => {
  const normalizedType = type || "all";
  const key = `project|${normalizedType}|${projectId ?? ""}`;
  return runDeduped(key, () => fetchDocumentsByProject(projectId, normalizedType));
};

export const getPlantDocumentList = async (type, plantId) => {
  const normalizedType = type || "all";
  const key = `plant|${normalizedType}|${plantId ?? ""}`;
  return runDeduped(key, () => loadPlantDocumentList(normalizedType, plantId));
};

export const updateDocumentList = async (updateDocument, { wbsLabel } = {}) => {
  const body = buildDocumentMutationBody(updateDocument, {
    isCreate: false,
    wbsLabel,
  });
  await secondAxios.patch(`/documents/${updateDocument.id}`, body);
  invalidateDocumentListCache();
};

export const createDocumentList = async (
  type,
  projectId,
  newDoc,
  { wbsLabel } = {},
) => {
  const body = buildDocumentMutationBody(
    { ...newDoc, type: newDoc.type ?? type },
    { isCreate: true, wbsLabel },
  );
  await secondAxios.post(`/documents/${projectId}`, body);
  invalidateDocumentListCache();
};

export const deleteDocumentList = async (id) => {
  try {
    await secondAxios.delete(`/documents/${id}`);
    invalidateDocumentListCache();
  } catch (error) {
    console.log(error);
  }
};
